import { lazy, Suspense, useEffect, useRef, useState, type RefObject } from 'react'
import { useMapContext } from './context/MapContext'
import TopBar from './components/TopBar/TopBar'
import CombinedBar from './components/CombinedBar/CombinedBar'
import SitePanel from './components/SitePanel/SitePanel'
import ListPanel from './components/ListPanel/ListPanel'
import DragDivider from './components/DragDivider/DragDivider'
import PrintSiteList from './components/PrintSiteList'
import LandingPage from './components/LandingPage/LandingPage'
import { prefersReducedMotion } from './utils/motion'
import { DEFAULT_LIST_WIDTH, fullExpandWidth, maxListPanelWidth } from './utils/listPanelWidth'
import { useWindowWidth } from './hooks/useWindowWidth'

// A static `import MapContainer from '...'` here would still pull maplibre-gl (~1MB) into the
// eagerly-evaluated module graph regardless of when AppLayout actually mounts — the idle-callback
// deferral below only delays *mounting*, not the network fetch, since ESM imports resolve at
// parse time. lazy() + dynamic import() makes the fetch itself wait until first render, so it
// only starts once shouldMountMap flips (see the idle callback in App below).
const MapContainer = lazy(() => import('./components/MapContainer/MapContainer'))

const COLLAPSED_WIDTH = 32

/** Matches the 400ms opacity transition on the screen wrappers, or fires immediately when the
 *  user has asked for reduced motion — there's no cross-fade to wait out in that case. */
function transitionFocusDelay(): number {
  return prefersReducedMotion() ? 0 : 420
}

/**
 * Moves focus to `ref`, unless the user got there first.
 *
 * These focus moves are on a timer tied to a screen transition, so a keyboard user who starts
 * Tabbing during the cross-fade would otherwise have focus yanked out from under them
 * mid-interaction. Only an untouched focus position (still on <body>, i.e. nothing focused since
 * the transition began) is safe to move.
 */
function focusIfUserHasNotMovedOn(ref: RefObject<HTMLElement | null>) {
  const active = document.activeElement
  if (active && active !== document.body) return
  ref.current?.focus()
}

function AppLayout({ onBackToLanding, mainRef, onAnnounce }: { onBackToLanding: () => void; mainRef: RefObject<HTMLElement | null>; onAnnounce: (msg: string) => void }) {
  const { selectedSite, viewMode } = useMapContext()
  // `requestedListWidth` is what the user last asked for (drag, keyboard, or a preset button);
  // `listPanelWidth` is that value clamped to the current viewport's ceiling, computed on every
  // render rather than stored. The cap is viewport-relative, so a width that was legal when it was
  // set goes oversized after the window shrinks or rotates — deriving it here re-clamps for free,
  // and keeping the requested value intact means widening the window restores the width the user
  // originally chose instead of leaving it stuck at whatever the smallest size allowed.
  const [requestedListWidth, setRequestedListWidth] = useState(DEFAULT_LIST_WIDTH)
  const lastExpandedWidth = useRef(DEFAULT_LIST_WIDTH)
  const windowWidth = useWindowWidth()
  const listPanelWidth = Math.min(requestedListWidth, maxListPanelWidth(windowWidth))

  // Switching Map/List replaces the whole <main> while focus stays on the toggle button, so
  // without this a screen reader user gets only the button's own aria-pressed change and no
  // indication that the content behind it swapped. The landing↔map transition already announces
  // and retitles (see handleSelect/handleBackToLanding below); this extends the same treatment to
  // the view toggle. Skipped on mount — the initial view was already announced by whichever
  // transition got us here, and re-announcing it on arrival would be noise.
  const isFirstRender = useRef(true)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    const name = viewMode === 'list' ? 'List view' : 'Map view'
    onAnnounce(`${name}.`)
    document.title = `${name} — Inglis Enabled Home Showcase`
  }, [viewMode, onAnnounce])

  function handleWidthChange(newWidth: number) {
    if (newWidth > COLLAPSED_WIDTH) lastExpandedWidth.current = newWidth
    setRequestedListWidth(newWidth)
  }

  function handleExpand() {
    setRequestedListWidth(lastExpandedWidth.current)
  }

  function handleFullExpand() {
    const w = fullExpandWidth(window.innerWidth)
    lastExpandedWidth.current = w
    setRequestedListWidth(w)
  }

  function handleMinimize() {
    setRequestedListWidth(DEFAULT_LIST_WIDTH)
    lastExpandedWidth.current = DEFAULT_LIST_WIDTH
  }

  return (
    <div className="app-layout viewport-h flex flex-col overflow-hidden bg-white">
      <div className="flex flex-col flex-1 overflow-hidden print:hidden">
        <TopBar onBackToLanding={onBackToLanding} />
        <CombinedBar />
        {/* id/tabIndex live on the actual <main> landmark (not the outer screen wrapper further
            down) so the skip link's next Tab press actually lands past TopBar/CombinedBar chrome
            instead of back at the top of it — the previous id placement made the skip link a
            functional no-op, since focusing the wrapper still tabs through the chrome first. */}
        <main ref={mainRef as RefObject<HTMLElement>} id="main-content" tabIndex={-1} className="flex flex-1 overflow-hidden relative outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-blue-500">
          {/* MapContainer's Suspense wrapper carries a stable `key` and stays at a consistent
              position across this toggle on purpose — the old version nested it differently per
              branch (inside a div alongside ListPanel+DragDivider vs. a bare sibling of
              SitePanel), so React saw a different tree shape each time and fully remounted the
              maplibre-gl WebGL map on every Map/List switch, resetting pan/zoom. Keying it lets
              React match it across renders regardless of what else is mounted around it. */}
          {viewMode === 'list' && (
            <ListPanel key="list-panel" width={listPanelWidth} onExpand={handleExpand} onFullExpand={handleFullExpand} onMinimize={handleMinimize} />
          )}
          <div key="map-wrapper" className={viewMode === 'list' ? 'hidden sm:flex flex-1 overflow-hidden' : 'flex flex-1 overflow-hidden'}>
            {viewMode === 'list' && (
              <DragDivider key="drag-divider" width={listPanelWidth} onWidthChange={handleWidthChange} />
            )}
            <Suspense fallback={null}>
              <MapContainer />
            </Suspense>
          </div>
          {viewMode !== 'list' && selectedSite && <SitePanel key="site-panel" />}
        </main>
      </div>
      <PrintSiteList />
    </div>
  )
}

export default function App() {
  const { setViewMode, viewMode } = useMapContext()
  const [screen, setScreen] = useState<'landing' | 'map'>('landing')
  const [liveMsg, setLiveMsg] = useState('')
  const mapFocusRef = useRef<HTMLElement>(null)
  const landingFocusRef = useRef<HTMLDivElement>(null)

  // AppLayout pulls in maplibre-gl (~1MB). We deliberately mount it behind the landing page so
  // it's ready by the time the user enters the map (see the "Map preloads..." comment below),
  // but we don't want that ~1MB import blocking the landing page's own first paint. Deferring the
  // mount to the next idle slot lets the landing page render immediately; the map then loads in
  // the background while the user is reading/choosing, same as before.
  const [shouldMountMap, setShouldMountMap] = useState(false)
  useEffect(() => {
    const idle = typeof window.requestIdleCallback === 'function'
      ? window.requestIdleCallback
      : (cb: () => void) => window.setTimeout(cb, 0)
    const cancelIdle = typeof window.cancelIdleCallback === 'function'
      ? window.cancelIdleCallback
      : window.clearTimeout
    const handle = idle(() => setShouldMountMap(true))
    return () => cancelIdle(handle)
  }, [])

  // Moves focus into the map screen once it's actually mounted. This used to fire off a bare
  // setTimeout(..., 420) from handleSelect() with no coordination with shouldMountMap above —
  // requestIdleCallback has no upper bound and is explicitly deprioritized under CPU load or on
  // a backgrounded/throttled tab, so on a slow device (or a tab briefly backgrounded right
  // before the click) the 420ms timeout could fire before AppLayout had mounted, leaving
  // mapFocusRef.current null and the focus() call a silent no-op. Keying this effect off both
  // screen and shouldMountMap means it only ever attempts focus once AppLayout is guaranteed to
  // exist, however long the idle callback actually took.
  useEffect(() => {
    if (screen !== 'map' || !shouldMountMap) return
    const timer = setTimeout(() => focusIfUserHasNotMovedOn(mapFocusRef), transitionFocusDelay())
    return () => clearTimeout(timer)
  }, [screen, shouldMountMap])

  function handleSelect() {
    setScreen('map')
    setLiveMsg('Map loaded. Use Tab to navigate.')
    document.title = 'Map — Inglis Enabled Home Showcase'
  }

  function handleBackToLanding() {
    setScreen('landing')
    // Otherwise a List-view visit leaves viewMode stuck at 'list' in MapProvider (which never
    // remounts across screen changes), so the *next* "Explore the Map" click from a fresh
    // landing visit silently re-opens List instead of the map.
    setViewMode('map')
    setLiveMsg('Returned to the start page.')
    document.title = 'Inglis Enabled Home Showcase'
    setTimeout(() => focusIfUserHasNotMovedOn(landingFocusRef), transitionFocusDelay())
  }

  return (
    <div className="app-root viewport-h" style={{ position: 'relative', width: '100vw', overflow: 'hidden' }}>
      {/* Skip navigation. One "skip to main content" link only ever solved the first hop; a
          screen-reader or keyboard user who wanted the map still had to tab past the filter bar,
          and one who wanted the filters had to walk back up. These name the app's actual regions,
          so the first Tab press on the page offers a jump to any of them. Each is visible on
          focus (see .skip-link in index.css) — sighted keyboard users get the same shortcut, which
          is the WCAG 2.4.1 intent rather than a screen-reader-only affordance.

          Map-screen only: the landing page has no regions to skip to, and rendering dead links to
          ids that don't exist there would announce targets that go nowhere. */}
      {screen === 'map' && (
        <nav aria-label="Skip navigation" className="skip-links">
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <a href="#filter-controls" className="skip-link">Skip to filters</a>
          {/* Rendered per view rather than always: the site list only exists in list view, and the
              map is hidden below the `sm` breakpoint while list view is open, so a static pair
              would offer at least one link to something not on screen. */}
          {viewMode === 'list' && <a href="#site-list" className="skip-link">Skip to site list</a>}
          <a href="#site-map" className="skip-link">Skip to map</a>
        </nav>
      )}
      {/* Screen reader live region for view transitions */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">{liveMsg}</div>

      {/* Map preloads behind the landing page. Before shouldMountMap flips true (a brief window,
          typically well under a second — see the idle-callback effect above) #main-content
          doesn't exist yet since it now lives on AppLayout's own <main>; the skip link is a no-op
          for that instant rather than permanently, which is the trade worth making versus the
          previous placement always resolving to a target that didn't actually skip the chrome. */}
      <div
        className="app-map-screen"
        inert={screen !== 'map'}
        style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          opacity: screen === 'map' ? 1 : 0,
          transition: 'opacity 400ms ease-in-out',
          pointerEvents: screen === 'map' ? 'auto' : 'none',
          outline: 'none',
        }}
      >
        {shouldMountMap && <AppLayout onBackToLanding={handleBackToLanding} mainRef={mapFocusRef} onAnnounce={setLiveMsg} />}
      </div>
      {/* Landing page */}
      <div
        className="app-landing-screen"
        inert={screen !== 'landing'}
        style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0, left: 0,
          opacity: screen === 'landing' ? 1 : 0,
          transition: 'opacity 400ms ease-in-out',
          pointerEvents: screen === 'landing' ? 'auto' : 'none',
        }}
      >
        <LandingPage onSelect={handleSelect} focusRef={landingFocusRef} />
      </div>
    </div>
  )
}
